import { appLocalStorage, appSessionStorage } from '../services';
import {
  checkoutGrandTotal,
  placeOrderMethodCode,
  restrictFreePaymentMethods,
} from '../../../shared/helpers/checkout-free-order';

const WALLET_PAYMENT_METHODS = ['multisafepay_applepay', 'multisafepay_googlepay'];

const isWalletPaymentMethod = (methodCode: string): boolean => {
  return WALLET_PAYMENT_METHODS.includes(methodCode);
};

const initialState = () => {
  return {
    couponCode: appSessionStorage.getItem('checkout-payment-coupon-code', ''),
    couponStatus: null,
    couponMessage: '',
    couponRequestIsLoading: false,

    paymentMethods: [],
    paymentMethodConfig: [],
    loadingPaymentData: false,
    selectedPaymentMethod: appSessionStorage.getItem('checkout-payment-selected-method', {}),
    selectedPaymentIssuer: appSessionStorage.getItem('checkout-payment-selected-issuer', {}),

    paymentDetails: appSessionStorage.getItem('checkout-payment-details', {}),
  };
};

const getters = {
  couponCode: (state) => state.couponCode,
  couponStatus: (state) => state.couponStatus,
  couponMessage: (state) => state.couponMessage,
  couponRequestIsLoading: (state) => state.couponRequestIsLoading,

  paymentMethods: (state) => state.paymentMethods,
  paymentMethodConfig: (state) => state.paymentMethodConfig,
  loadingPaymentData: (state) => state.loadingPaymentData,
  selectedPaymentMethod: (state) => state.selectedPaymentMethod,
  selectedPaymentIssuer: (state) => state.selectedPaymentIssuer,

  paymentDetails: (state) => state.paymentDetails,
};

const mutations = {
  SET_COUPON_CODE(state, couponCode) {
    state.couponCode = couponCode;
    appSessionStorage.setItem('checkout-payment-coupon-code', couponCode);
  },
  SET_COUPON_STATUS(state, couponStatus) {
    state.couponStatus = couponStatus;
  },
  SET_COUPON_MESSAGE(state, couponMessage) {
    state.couponMessage = couponMessage;
  },
  SET_COUPON_REQUEST_IS_LOADING(state, couponRequestIsLoading) {
    state.couponRequestIsLoading = couponRequestIsLoading;
  },
  SET_PAYMENT_METHODS(state, paymentMethods) {
    state.paymentMethods = paymentMethods;
  },
  SET_PAYMENT_METHOD_CONFIG(state, paymentMethodConfig) {
    state.paymentMethodConfig = paymentMethodConfig;
  },
  SET_LOADING_PAYMENT_DATA(state, loadingPaymentData) {
    state.loadingPaymentData = loadingPaymentData;
  },
  SET_SELECTED_PAYMENT_METHOD(state, selectedPaymentMethod) {
    state.selectedPaymentMethod = selectedPaymentMethod;
  },
  SET_SELECTED_PAYMENT_ISSUER(state, selectedPaymentIssuer) {
    state.selectedPaymentIssuer = selectedPaymentIssuer;
    appSessionStorage.setItem('checkout-payment-selected-issuer', selectedPaymentIssuer);
  },
  CLEAR_PAYMENT_DETAILS(state) {
    state.paymentDetails = {};
    appSessionStorage.removeItem('checkout-payment-details');
  },
  ADD_PAYMENT_DETAILS(state, paymentDetail) {
    state.paymentDetails = { ...state.paymentDetails, ...paymentDetail };
    appSessionStorage.setItem('checkout-payment-details', state.paymentDetails);
  },
  REMOVE_PAYMENT_DETAILS(state, paymentDetail) {
    if (state.paymentDetails.hasOwnProperty(paymentDetail)) {
      delete state.paymentDetails[paymentDetail];
    }
    appSessionStorage.setItem('checkout-payment-details', state.paymentDetails);
  },
};

const actions = {
  async setSelectedPaymentMethod({ commit, dispatch }, paymentMethod) {
    commit('SET_SELECTED_PAYMENT_METHOD', paymentMethod);
    appSessionStorage.setItem('checkout-payment-selected-method', paymentMethod);
  },
  async setSelectedPaymentIssuer({ commit }, paymentIssuer) {
    commit('SET_SELECTED_PAYMENT_ISSUER', paymentIssuer);
  },
  async setPaymentMethod({ rootGetters, dispatch, commit }, paymentMethod) {
    let additionalData = {};
    const customerEmail = rootGetters['CheckoutGlobal/customerEmail']
      || appLocalStorage.getItem('checkout-customer-email', '')
      || '';

    if (!customerEmail) {
      return;
    }

    commit('CheckoutTotals/SET_TOTALS_AVAILABLE', false, { root: true });

    const [_billingAddress, _shippingAddress] = rootGetters['CheckoutAddress/getAddressData'];

    if (isWalletPaymentMethod(paymentMethod['code'])) {
      additionalData = { ...additionalData, transaction_type: 'redirect' };
    }

    try {
      await this.$solarClient.post(`/api/checkout/payment/setPaymentInformation`, {
        email: customerEmail,
        paymentMethod: {
          method: paymentMethod['code'],
          additional_data: additionalData,
        },
        billingAddress: _billingAddress,
      });

      // Collect totals.
      await dispatch('CheckoutTotals/fetchCartTotals', null, { root: true });

      if (0 === rootGetters['CheckoutTotals/cartTotals']['extension_attributes']?.['valid_stock']) {
        window.location.href = '/checkout/cart';
      }
    } catch (err) {
      commit('CheckoutTotals/SET_TOTALS_AVAILABLE', true, { root: true });
    }
  },
  async collectPaymentMethods({ rootGetters, commit, dispatch }) {
    try {
      commit('SET_LOADING_PAYMENT_DATA', true);

      const { method_code, carrier_code } = rootGetters['CheckoutShipping/shippingMethod'];
      const [_billingAddress, _shippingAddress] = rootGetters['CheckoutAddress/getAddressData'];
      const _extensionAttributes = rootGetters['CheckoutGlobal/getExtensionAttributes'];

      const { data } = await this.$solarClient.post('/api/checkout/payment/collectPaymentMethods', {
        addressInformation: {
          shippingAddress: _shippingAddress,
          billingAddress: _billingAddress,
          shippingMethodCode: method_code,
          shippingCarrierCode: carrier_code,
          extensionAttributes: _extensionAttributes,
        },
      });

      const totals = data?.['totals'];
      const paymentMethods = restrictFreePaymentMethods(
        Array.isArray(data?.['payment_methods']) ? data['payment_methods'] : [],
        checkoutGrandTotal(totals?.['grand_total'] ?? rootGetters['CheckoutTotals/cartTotals']?.['grand_total'])
      );

      commit('SET_PAYMENT_METHODS', paymentMethods);
      commit('SET_PAYMENT_METHOD_CONFIG', data['payment_config'] || {});
      if (paymentMethods.length > 0) {
        commit('SET_SELECTED_PAYMENT_METHOD', paymentMethods[0]);
      }

      if (totals && typeof totals === 'object') {
        commit('CheckoutTotals/SET_CART_TOTALS', totals, { root: true });
        commit('CheckoutTotals/SET_CART_ITEMS', Array.isArray(totals['items']) ? totals['items'] : [], {
          root: true,
        });
        commit('CheckoutTotals/SET_TOTALS_AVAILABLE', true, { root: true });
      } else {
        // Totals missing from collectPaymentMethods response; fetch separately.
        await dispatch('CheckoutTotals/fetchCartTotals', null, { root: true });
      }
    } catch (err) {
      // Payment methods call failed; attempt standalone totals fetch as fallback.
      await dispatch('CheckoutTotals/fetchCartTotals', null, { root: true });
    } finally {
      commit('SET_LOADING_PAYMENT_DATA', false);
    }
  },

  async placeOrder({ rootGetters, getters, dispatch, rootState }) {
    const [_billingAddress] = rootGetters['CheckoutAddress/getAddressData'];
    const customerEmail = rootGetters['CheckoutGlobal/customerEmail']
      || appLocalStorage.getItem('checkout-customer-email', '')
      || '';

    if (!customerEmail) {
      throw new Error('Customer email is required to place an order.');
    }

    const orderComment = (rootState.CheckoutGlobal?.orderComment || '').trim();
    if (orderComment) {
      try {
        await this.$solarClient.post('/api/checkout/order-comment', {
          comment: orderComment,
        });
      } catch (e) {
        // Order comment should not block checkout.
      }
    }

    if (rootState.CheckoutGlobal?.newsletterSubscribe) {
      try {
        await this.$solarClient.post('/api/checkout/newsletter/subscribe', {
          email: customerEmail,
        });
      } catch (e) {
        // Newsletter subscribe should not block checkout.
      }
    }

    const methodCode = placeOrderMethodCode(
      getters.selectedPaymentMethod['code'],
      getters.paymentMethods || [],
      checkoutGrandTotal(rootGetters['CheckoutTotals/cartTotals']?.['grand_total'])
    );
    let additionalData = { ...(getters.paymentDetails || {}) };
    if (isWalletPaymentMethod(methodCode)) {
      additionalData = { ...additionalData, transaction_type: 'redirect' };
    }

    return await this.$solarClient.post('/api/checkout/payment/placeOrder', {
      email: customerEmail,
      shippingOptions: {},
      paymentMethod: {
        method: methodCode,
        additional_data: additionalData,
      },
      billingAddress: _billingAddress,
    });
  },

  async getCouponCode({ rootState, rootGetters, commit }) {
    try {
      const { data } = await this.$solarClient.get('/api/checkout/payment/coupons');

      if (data.length) {
        commit('SET_COUPON_CODE', data);
        commit('SET_COUPON_STATUS', true);
        return true;
      }

      return false;
    } catch (e) {
      return false;
    }
  },
  async applyCouponCode({ commit, dispatch }, couponCode) {
    commit('SET_COUPON_STATUS', false);
    commit('SET_COUPON_REQUEST_IS_LOADING', true);
    try {
      const { data } = await this.$solarClient.put(`/api/checkout/payment/coupons/${couponCode}`);

      if (data['couponStatus']) {
        commit('SET_COUPON_CODE', couponCode);
        commit('SET_COUPON_STATUS', true);
      }

      commit('CheckoutTotals/SET_CART_TOTALS', data['totals'], { root: true });
      commit('CheckoutTotals/SET_CART_ITEMS', data['totals']['items'], {
        root: true,
      });
      await dispatch('collectPaymentMethods');
    } catch ({ response }) {
      commit('SET_COUPON_MESSAGE', response.data.message);
      commit('SET_COUPON_STATUS', false);
    } finally {
      commit('SET_COUPON_REQUEST_IS_LOADING', false);
    }
  },
  async removeCouponCode({ commit, dispatch }) {
    commit('SET_COUPON_REQUEST_IS_LOADING', true);
    try {
      const { data } = await this.$solarClient.delete(`/api/checkout/payment/coupons`);
      commit('SET_COUPON_CODE', '');
      commit('SET_COUPON_STATUS', null);

      commit('CheckoutTotals/SET_CART_TOTALS', data['totals'], { root: true });
      commit('CheckoutTotals/SET_CART_ITEMS', data['totals']['items'], {
        root: true,
      });
      await dispatch('collectPaymentMethods');
    } catch (e) {
      commit('SET_COUPON_MESSAGE', 'De coupon code kon niet worden geannuleerd, probeer het later opnieuw.');
    } finally {
      commit('SET_COUPON_REQUEST_IS_LOADING', false);
    }
  },
};

const state = initialState();

const CheckoutPayment = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

export default CheckoutPayment;
